import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Lock, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

export default function Procurement() {
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [items, setItems] = useState([{ name: "", category: "equipment", qty: 1, unit_price: 0 }]);
  const [notes, setNotes] = useState("");

  const reload = async () => {
    const { data } = await api.get("/procurement/orders");
    setOrders(data);
  };
  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      if (r.data[0]) setProjectId(r.data[0].id);
    });
    reload();
  }, []);

  const addLine = () => setItems([...items, { name: "", category: "equipment", qty: 1, unit_price: 0 }]);
  const setLine = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const removeLine = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!projectId) return toast.error("Select a project first");
    try {
      await api.post("/procurement/orders", { project_id: projectId, items, notes });
      toast.success("Order FROZEN. Routed to Line Producer.");
      setItems([{ name: "", category: "equipment", qty: 1, unit_price: 0 }]);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submit failed");
    }
  };

  const approveOrder = async (orderId, order) => {
    const approved = order.items.map((_, i) => i);
    try {
      await api.post(`/procurement/orders/${orderId}/lp-approve`, {
        approved_items: approved, trimmed_items: [], rejection_note: "",
      });
      toast.success("Order routed to vendor");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const total = items.reduce((s, i) => s + (i.qty * i.unit_price), 0);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="procurement-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Automated Procurement Gate</div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">HOD → LP Approval</h1>
        <p className="mt-3 text-sm text-white/55 max-w-2xl">
          Once you submit, the order is <span className="text-[#F5B82E] uppercase">frozen</span>.
          Vendor routing is blocked until the Line Producer trims & approves.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Cart */}
        <div className="cs-card rounded-2xl p-6">
          <div className="font-display text-xl uppercase tracking-tight mb-4">New Order Cart</div>

          <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">Project</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="bg-black/40 border-white/15 mt-2" data-testid="order-project"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="mt-5 space-y-3" data-testid="order-items">
            {items.map((it, i) => (
              <div key={`item-${i}`} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  <Input placeholder="Item name" value={it.name} data-testid={`item-name-${i}`}
                    onChange={(e) => setLine(i, "name", e.target.value)} className="bg-black/40 border-white/15 h-10" />
                </div>
                <div className="col-span-3">
                  <Select value={it.category} onValueChange={(v) => setLine(i, "category", v)}>
                    <SelectTrigger className="bg-black/40 border-white/15 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equipment">Equipment</SelectItem>
                      <SelectItem value="labor">Labor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1.5">
                  <Input type="number" placeholder="Qty" value={it.qty} data-testid={`item-qty-${i}`}
                    onChange={(e) => setLine(i, "qty", parseInt(e.target.value) || 1)}
                    className="bg-black/40 border-white/15 h-10" />
                </div>
                <div className="col-span-2">
                  <Input type="number" placeholder="₹/unit" value={it.unit_price} data-testid={`item-price-${i}`}
                    onChange={(e) => setLine(i, "unit_price", parseFloat(e.target.value) || 0)}
                    className="bg-black/40 border-white/15 h-10" />
                </div>
                <button onClick={() => removeLine(i)} className="text-white/40 hover:text-red-400 col-span-1 h-10">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
          <Button onClick={addLine} variant="outline" className="mt-3 bg-transparent border-white/15 text-white hover:bg-white hover:text-black" data-testid="add-line-btn">
            <Plus size={14} className="mr-2" /> Add line item
          </Button>

          <div className="mt-5">
            <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">Notes for LP</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
              className="bg-black/40 border-white/15 mt-2" data-testid="order-notes" />
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
            <div className="text-sm text-white/60">Estimated total</div>
            <div className="font-display text-2xl tabular-nums" style={{ color: "var(--association-theme)" }}>
              ₹{total.toLocaleString("en-IN")}
            </div>
          </div>

          <Button onClick={submit} className="btn-themed w-full mt-5 h-12 rounded-full uppercase tracking-[0.25em] text-xs" data-testid="submit-order-btn">
            <Lock size={14} className="mr-2" /> Submit Order (Freeze + Route to LP)
          </Button>
        </div>

        {/* LP queue */}
        <div className="cs-card rounded-2xl p-6">
          <div className="font-display text-xl uppercase tracking-tight mb-4">Line Producer Queue</div>
          {orders.length === 0 ? (
            <div className="text-center py-10 text-white/40 text-sm">No orders yet.</div>
          ) : (
            <div className="space-y-4" data-testid="order-queue">
              {orders.map((o) => (
                <div key={o.id} className="border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] tracking-[0.25em] uppercase">
                      <span className={o.status === "frozen_awaiting_lp" ? "text-[#F5B82E]" : "text-emerald-400"}>
                        {o.status.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div className="text-xs text-white/50">{new Date(o.created_at).toLocaleString()}</div>
                  </div>
                  <div className="mt-2 text-sm">
                    {o.items.length} item(s) · ₹{o.total_inr.toLocaleString("en-IN")}
                  </div>
                  <ul className="mt-2 text-xs text-white/55 space-y-0.5">
                    {o.items.map((it, idx) => (
                      <li key={`${o.id}-item-${idx}`}>· {it.name} × {it.qty} @ ₹{it.unit_price}</li>
                    ))}
                  </ul>
                  {o.status === "frozen_awaiting_lp" && (
                    <Button onClick={() => approveOrder(o.id, o)} className="btn-themed w-full mt-4 h-10 rounded-full uppercase tracking-[0.25em] text-xs"
                            data-testid={`approve-order-${o.id}`}>
                      <CheckCircle2 size={14} className="mr-2" /> Approve & Route to Vendor
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
